/**
 * CrmListLayout — Shared layout shell for all CRM list/pipeline views.
 *
 * Composes PageLayout with CRM-specific toolbar rendering (search, filters,
 * chips). View-specific content is passed as children.
 *
 * All CRM views must use this layout to ensure consistent spacing,
 * borders, and responsive behavior.
 */

import type { FC } from 'react';
import { useMemo } from 'react';

import type { HeaderAction } from '@/lib/base/page-layout/page-layout';
import { PageLayout } from '@/lib/base/page-layout/page-layout';
import {
  ToolbarFilter,
  ToolbarSearch,
  ToolbarSection,
} from '@/lib/base/page-layout/toolbar';
import { useSearchInput } from '@/lib/base/page-layout/use-search-input';
import type {
  FilterCondition,
  FilterConditionGroup,
} from '@/lib/crm/filters/types';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { isFilterConditionValid } from '@/lib/crm/list/components/filterConditionDisplay';
import { FilterConditionsTooltip } from '@/lib/crm/list/components/FilterConditionsTooltip';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CrmListLayoutProps {
  // --- Header Row 1 ---

  /** Entity type display name (e.g., "Contacts", "Deals") */
  entityTitle: string;
  /** Icon component for the entity type */
  entityIcon?: FC<{ className?: string }>;
  /**
   * Total record count.
   * @deprecated No longer shown in the header — the table renders its own
   * count at the bottom. Accepted for backwards compatibility but ignored.
   */
  recordCount?: number;
  /**
   * Content rendered inline to the right of the title (e.g. the saved-view
   * tab strip). Forwarded to PageLayout's `titleTrailing` slot.
   */
  titleTrailing?: React.ReactNode;
  /**
   * @deprecated No longer rendered (see `recordCount`). Accepted for
   * backwards compatibility but ignored.
   */
  countLabel?: string;
  /**
   * @deprecated No longer rendered (see `recordCount`). Accepted for
   * backwards compatibility but ignored.
   */
  countLabelSingular?: string;
  /**
   * Structured action buttons for the right side of the header.
   * Desktop: all inline. Mobile: primary visible, secondary in overflow.
   */
  actions?: HeaderAction[];

  // --- Toolbar Row 2: Search ---

  /** Current search value */
  searchValue?: string;
  /** Callback when search changes */
  onSearch?: (query: string) => void;
  /** Search placeholder text */
  searchPlaceholder?: string;

  // --- Toolbar Row 2: Filter ---

  /** Whether filters are active */
  hasActiveFilters?: boolean;
  /** Callback when filter button clicked */
  onFilterClick?: () => void;
  /** Active filter conditions to display as chips */
  filterConditions?: FilterCondition[];
  /**
   * Applied filters grouped by their OR/AND structure (OR between groups, AND
   * within a group). When provided, the filter-button tooltip renders that
   * structure explicitly. Omit it — as the pipeline board does, having no
   * OR-groups — and the tooltip falls back to a single implicit AND group
   * built from `filterConditions`.
   */
  filterConditionGroups?: FilterConditionGroup[];
  /** Property labels for filter chips */
  propertyLabels?: Record<string, string>;
  /** Property schemas for filter chip value formatting */
  propertySchemas?: Record<string, PropertyTypeSchema>;
  /** Association labels for filter chips */
  associationLabels?: Record<string, string>;
  /** Org ID — used to hydrate association badges in the filter tooltip */
  orgId: number;
  /** Callback when a filter chip is removed */
  onRemoveFilter?: (conditionId: string) => void;

  // --- Toolbar Row 2: Extra content ---

  /** Content rendered before the search/filter toolbar section (e.g., user dropdown) */
  toolbarLeading?: React.ReactNode;

  /**
   * Extra toolbar content rendered after filter chips (e.g., sort dropdown,
   * pipeline selector, view toggle). Pushed to the right via flex spacer.
   */
  toolbarExtra?: React.ReactNode;

  /**
   * Content pinned to the far RIGHT of the toolbar row (e.g. an embedded
   * list's primary create actions, which have nowhere else to go once the
   * page-title action row is dropped in `embedded` mode). Right-aligned via
   * `ml-auto` so it sits opposite the leading/search controls.
   */
  toolbarTrailing?: React.ReactNode;

  // --- Content ---

  /** View content (table, pipeline board, etc.) */
  children: React.ReactNode;
  /** Optional className on the outer container */
  className?: string;
  /** Optional className applied to the content area (below header/toolbar) */
  contentClassName?: string;
  /** Embedded mode — drops the page title row + outer padding (see PageLayout). */
  embedded?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CrmListLayout({
  // Header
  entityTitle,
  entityIcon,
  titleTrailing,
  actions,
  // Search
  searchValue = '',
  onSearch,
  searchPlaceholder = 'Search',
  // Filter
  hasActiveFilters: _hasActiveFilters = false,
  onFilterClick,
  filterConditions = [],
  filterConditionGroups,
  propertyLabels = {},
  propertySchemas = {},
  associationLabels = {},
  orgId,
  onRemoveFilter: _onRemoveFilter,
  // Toolbar
  toolbarLeading,
  toolbarExtra,
  toolbarTrailing,
  // Content
  children,
  className,
  contentClassName,
  embedded = false,
}: CrmListLayoutProps) {
  const {
    searchTerm,
    inputRef,
    handleSearchChange,
    handleSearchBlur,
    isPending: isSearchPending,
  } = useSearchInput({ searchValue, onSearch });

  // Normalize the applied filters into OR-groups of valid AND-conditions.
  // When only the flat `filterConditions` are supplied (e.g. the pipeline
  // board, which has no OR-groups), wrap them in a single implicit AND group
  // so the tooltip has one rendering path. Groups left empty after dropping
  // value-less conditions are removed entirely.
  const renderGroups = useMemo<FilterConditionGroup[]>(() => {
    const source =
      filterConditionGroups && filterConditionGroups.length > 0
        ? filterConditionGroups
        : [{ id: 'all', conditions: filterConditions }];
    return source
      .map(group => ({
        ...group,
        conditions: group.conditions.filter(isFilterConditionValid),
      }))
      .filter(group => group.conditions.length > 0);
  }, [filterConditionGroups, filterConditions]);

  // Total valid conditions across every group — drives the button badge count.
  const totalValidCount = useMemo(
    () => renderGroups.reduce((sum, group) => sum + group.conditions.length, 0),
    [renderGroups]
  );

  // Tooltip content for active filters — renders the OR/AND group structure so
  // the filter logic is legible from the filter button alone (there is no
  // longer a separate chips bar). See FilterConditionsTooltip.
  const filterTooltipTitle = useMemo(() => {
    if (totalValidCount === 0) {
      return undefined;
    }
    return (
      <FilterConditionsTooltip
        groups={renderGroups}
        propertyLabels={propertyLabels}
        propertySchemas={propertySchemas}
        associationLabels={associationLabels}
        orgId={orgId}
      />
    );
  }, [
    renderGroups,
    totalValidCount,
    propertyLabels,
    propertySchemas,
    associationLabels,
    orgId,
  ]);

  // The record count is intentionally not shown in the header — the table
  // renders its own count at the bottom, and omitting it here gives the
  // saved-view tab strip more room.

  // Capitalize entityIcon for JSX usage
  const EntityIcon = entityIcon;

  return (
    <PageLayout
      title={entityTitle}
      icon={
        EntityIcon ? (
          <EntityIcon className='size-5 text-fg-quaternary' />
        ) : undefined
      }
      titleTrailing={titleTrailing}
      actions={actions}
      // List/board headers carry a lot (title + saved-view tabs + several
      // actions), so keep them inline only at lg+ and collapse secondaries
      // into the overflow menu below that to avoid crowding on medium widths.
      collapseActionsBreakpoint='lg'
      toolbar={
        <>
          {toolbarLeading}
          <ToolbarSection>
            {/* Search */}
            {onSearch && (
              <ToolbarSearch
                value={searchTerm}
                onChange={handleSearchChange}
                inputRef={inputRef}
                onBlur={handleSearchBlur}
                placeholder={searchPlaceholder}
                isSearching={isSearchPending}
              />
            )}

            {/* Filter Button */}
            {onFilterClick && (
              <ToolbarFilter
                activeCount={totalValidCount}
                onClick={onFilterClick}
                tooltip={filterTooltipTitle}
              />
            )}
          </ToolbarSection>

          {/* Extra toolbar content (e.g., sort dropdown, pipeline selector) */}
          {toolbarExtra}

          {/* Far-right actions (e.g. embedded list create buttons). */}
          {toolbarTrailing && (
            <div className='ml-auto flex items-center gap-2'>
              {toolbarTrailing}
            </div>
          )}
        </>
      }
      toolbarBorderBottom={false}
      className={className}
      contentClassName={contentClassName}
      embedded={embedded}
    >
      {children}
    </PageLayout>
  );
}
